import { AwaitContainer } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAllAttributes, type AttributeListParams } from '@/entities/attributes';
import {
  getKmContextLevelKind,
  getKmContextUpperSources,
  splitKmContextItems,
  useKmContext,
  type KmContextLevel,
  type KmContextSource,
} from '@/entities/expert-report-km';
import { useHasPermission } from '@/entities/profile';
import { KmInheritedContextList } from './KmInheritedContextList';
import { KmOwnContextList } from './KmOwnContextList';
import { useChangeKmContext } from './useChangeKmContext';
import type { KmContextAttributeOption } from './types';

type KmContextSectionProps = {
  reportId: number;
  /** Группа, чей контекст показывается; без неё и без единицы оценивания — общий контекст заключения */
  groupId?: number;
  /** Единица оценивания, чей контекст показывается */
  assessmentId?: number;
  /** Правка закрыта самой карточкой независимо от права — при просмотре прежней версии заключения */
  isReadOnly?: boolean;
};

/** Назначаемые контекстом атрибуты: верхнего уровня справочника и не удалённые — те же, что включаются в группу атрибутов */
const ASSIGNABLE_ATTRIBUTES_PARAMS: AttributeListParams = { 'parent-struct-code': 'SIMPLE', 'is-deleted': false };

const OWN_CONTEXT_TITLE_KEYS = {
  report: 'km.reportContext',
  group: 'km.groupContext',
  assessment: 'km.assessmentContext',
} as const;

/**
 * Контекст оценки в карточке уровня заключения на модели знаний. Назначение и снятие атрибута
 * уходят на сервер сразу и в сохранение карточки не входят: у карточек уровней разные формы, и
 * контекст не должен ждать ни одну из них.
 */
export const KmContextSection: FC<KmContextSectionProps> = ({
  reportId,
  groupId,
  assessmentId,
  isReadOnly = false,
}) => {
  const { t } = useTranslation('expertReport');
  const allowAdmin = useHasPermission('ADMIN_EXPERT_REPORT');
  const canEdit = allowAdmin && !isReadOnly;
  const level = useMemo<KmContextLevel>(() => ({ reportId, groupId, assessmentId }), [reportId, groupId, assessmentId]);
  const levelKind = getKmContextLevelKind(level);
  const { data: context, isLoading } = useKmContext(level);
  // Справочник нужен только выбору: свои атрибуты показываются по самому контексту
  const { data: attributesData } = useAllAttributes(ASSIGNABLE_ATTRIBUTES_PARAMS, canEdit);
  const changeContext = useChangeKmContext();

  const { own, inherited } = useMemo(() => splitKmContextItems(context?.items ?? [], level), [context, level]);

  const upperSourcesByCode = useMemo(() => {
    const entries = own.map((item) => [item.attributeCode, getKmContextUpperSources(item, level)] as const);
    return new Map<string, KmContextSource[]>(entries.filter(([, sources]) => sources.length > 0));
  }, [own, level]);

  const ownOptions = useMemo<KmContextAttributeOption[]>(
    () => own.map((item) => ({ code: item.attributeCode, name: item.attributeName })),
    [own],
  );

  // Строки списка строятся только по перечню, поэтому свои атрибуты входят в него наравне со справочником
  const allOptions = useMemo(() => {
    const optionsByCode = new Map(ownOptions.map((option) => [option.code, option]));
    for (const attribute of attributesData?.attributes ?? []) {
      optionsByCode.set(attribute.code, {
        code: attribute.code,
        name: attribute.name,
        description: attribute.description,
      });
    }
    return [...optionsByCode.values()];
  }, [ownOptions, attributesData]);

  const handleChange = (next: KmContextAttributeOption[]) => changeContext(level, own, next);

  return (
    <section>
      <AwaitContainer isLoading={isLoading} className="flex flex-col gap-4">
        {/* Число — по всему контексту уровня: у единицы это предпросмотр, у группы — то, что получат вложенные */}
        <p className="text-sm text-muted-foreground">{t('km.contextCount', { count: context?.items.length ?? 0 })}</p>
        <div className="flex flex-col gap-6">
          <KmOwnContextList
            title={t(OWN_CONTEXT_TITLE_KEYS[levelKind])}
            value={ownOptions}
            allItems={allOptions}
            upperSourcesByCode={upperSourcesByCode}
            onChange={handleChange}
            disabled={!canEdit}
          />
          {levelKind !== 'report' && <KmInheritedContextList items={inherited} />}
        </div>
      </AwaitContainer>
    </section>
  );
};
